import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { AUTH } from '@/constants/testIds';
import ThemeToggle from '@/components/ThemeToggle';
import NotificationBell from '@/components/NotificationBell';
import useInspectorLocationTracker from '@/hooks/useInspectorLocationTracker';
import { LogOut, ShieldCheck } from 'lucide-react';

const ROLE_CONFIG = {
    client: {
        label: 'Panel Cliente principal',
        headerClass: 'panel-header-client',
        accent: '#1F8FB8',
        nav: [
            { to: '/client', label: 'Inicio' },
            { to: '/client/requests/new', label: 'Nueva solicitud' },
        ],
    },
    provider: {
        label: 'Panel Proveedor',
        headerClass: 'panel-header-provider',
        accent: '#E08B3C',
        nav: [
            { to: '/provider', label: 'Inicio' },
            { to: '/provider/requests/new', label: 'Nuevo distintivo' },
        ],
    },
    inspector: {
        label: 'Panel Auditor',
        headerClass: 'panel-header-inspector',
        accent: '#1E9E47',
        nav: [
            { to: '/inspector', label: 'Mis auditorías' },
        ],
    },
    admin: {
        label: 'Panel Administrador',
        headerClass: 'panel-header-admin',
        accent: '#0A0A0A',
        nav: [
            { to: '/admin', label: 'Dashboard' },
            { to: '/admin/users', label: 'Usuarios' },
            { to: '/admin/settings/certifications', label: 'Normas' },
            { to: '/admin/certifications', label: 'Distintivos' },
            { to: '/admin/catalog', label: 'Catálogo' },
        ],
    },
};

export default function DashboardLayout({ role, children }) {
    const { user, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const cfg = ROLE_CONFIG[role];

    // Tracking automático de ubicación cuando el rol es inspector (envía cada 60 s)
    useInspectorLocationTracker({ enabled: user?.role === 'inspector', intervalMs: 60000 });

    React.useEffect(() => {
        if (!user) navigate('/login');
        else if (user.role !== role) navigate(`/${user.role}`);
    }, [user, role, navigate]);

    if (!user || !cfg) return null;

    return (
        <div className="min-h-screen bg-white" data-testid={`${role}-layout`}>
            <header className={`${cfg.headerClass} border-b border-black/20`}>
                <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Link to="/" className="flex items-center">
                            <img src="https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/6tkmr7v8_3.png" alt="AIGIS CERTIFICATE · Safety Solutions" className="h-14 w-auto object-contain" />
                        </Link>
                        <span className="hidden md:inline text-[10px] uppercase tracking-[0.25em] opacity-80 border border-white/30 px-2 py-1">
                            {cfg.label}
                        </span>
                    </div>
                    <nav className="hidden md:flex items-center gap-6">
                        {cfg.nav.map((n) => {
                            const active = location.pathname === n.to;
                            return (
                                <Link
                                    key={n.to}
                                    to={n.to}
                                    className={`text-sm font-medium transition-colors ${
                                        active ? 'text-white' : 'text-white/70 hover:text-white'
                                    }`}
                                    data-testid={`nav-${n.to.replace(/\//g, '-')}`}
                                >
                                    {n.label}
                                </Link>
                            );
                        })}
                    </nav>
                    <div className="flex items-center gap-4">
                        <ThemeToggle variant="onDark" />
                        <NotificationBell variant="onDark" />
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-medium">{user.name}</div>
                            <div className="text-[11px] opacity-70">{user.email}</div>
                        </div>
                        <Link
                            to="/profile"
                            data-testid="nav-profile"
                            className="border border-white/40 hover:bg-white/10 px-3 py-2 transition-colors flex items-center gap-2 text-sm"
                            title="Perfil y seguridad"
                        >
                            <ShieldCheck className="w-4 h-4" /> <span className="hidden sm:inline">Perfil</span>
                        </Link>
                        <button
                            onClick={logout}
                            data-testid={AUTH.logoutButton}
                            className="border border-white/40 hover:bg-white/10 px-3 py-2 transition-colors flex items-center gap-2 text-sm"
                        >
                            <LogOut className="w-4 h-4" /> Salir
                        </button>
                    </div>
                </div>
                <div className="md:hidden border-t border-white/20 px-6 py-2 flex gap-4 overflow-x-auto">
                    {cfg.nav.map((n) => (
                        <Link key={n.to} to={n.to} className="text-xs whitespace-nowrap py-1 border-b-2 border-transparent hover:border-white">
                            {n.label}
                        </Link>
                    ))}
                </div>
            </header>
            <main className="max-w-[1400px] mx-auto px-6 py-8">{children}</main>
        </div>
    );
}

export { ROLE_CONFIG };
